// Full-screen entrance video player. Used for the boot intro AND for
// settings-triggered manual replays. Caller chooses the src; the two
// canonical paths are exported as INTRO_VIDEO_FIRST / INTRO_VIDEO_SECOND.
//
// Starts muted per browser autoplay policy; the FIRST user input
// (key/click/touch) is consumed to unmute instead of skipping —
// subsequent inputs skip. Fades the entire overlay from opacity 1 → 0
// between 7s and 10s of playback with a steep ease-in so the fade
// reads as "intense and fast" instead of a slow linear dissolve.
//
// The procedural music engine is hard-muted via the intro mute lock
// for the duration of the video so the entrance audio plays clean.
// Music is restored when the natural fade starts (so it rises as the
// video fades) or immediately on skip.
//
// An in-flight guard prevents two overlays from stacking if the
// caller invokes twice in quick succession; otherwise the function
// is fully re-callable across a session.
//
// Returns a Promise that resolves once the overlay is removed.

import { setIntroMusicMute } from '../audio/synth';

export const INTRO_VIDEO_FIRST  = '/intro/entrance.mp4';
export const INTRO_VIDEO_SECOND = '/intro/entrance2.mp4';

const FADE_START_SEC = 7;
const FADE_DURATION_SEC = 3;
const FADE_EASING = 'cubic-bezier(0.85, 0, 0.95, 0.2)';
// Music ramp-in durations. Natural end = match the visual fade so
// audio and visuals cross over the same window. Skip = a snappier
// fade so the player isn't sitting through 3 s of music swell after
// they've actively dismissed the cutscene.
const MUSIC_FADE_NATURAL_MS = FADE_DURATION_SEC * 1000;
const MUSIC_FADE_SKIP_MS = 600;
// Hard cap so a stalled / 404'd video never hard-blocks boot. 15 s
// covers a 10 s entrance plus generous buffer for the metadata fetch.
const MAX_LIFETIME_MS = 15_000;

// Cubic-bezier evaluator. Mirrors the CSS `cubic-bezier(p1x,p1y,p2x,p2y)`
// timing function so we can drive `video.volume` along the same curve
// the visual opacity transition uses (CSS doesn't transition the
// HTMLMediaElement.volume property, so we hand-evaluate it).
// Newton-Raphson root-finding for solveCurveX, capped at 8 iters —
// converges to <1e-6 well before that for normal cases.
function cubicBezierEasing(p1x: number, p1y: number, p2x: number, p2y: number): (x: number) => number {
  const cx = 3 * p1x;
  const bx = 3 * (p2x - p1x) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * p1y;
  const by = 3 * (p2y - p1y) - cy;
  const ay = 1 - cy - by;
  const sampleCurveX = (t: number): number => ((ax * t + bx) * t + cx) * t;
  const sampleCurveY = (t: number): number => ((ay * t + by) * t + cy) * t;
  const sampleDerivativeX = (t: number): number => (3 * ax * t + 2 * bx) * t + cx;
  return (x: number): number => {
    const clamped = x < 0 ? 0 : x > 1 ? 1 : x;
    let t = clamped;
    for (let i = 0; i < 8; i++) {
      const cur = sampleCurveX(t) - clamped;
      if (Math.abs(cur) < 1e-6) return sampleCurveY(t);
      const d = sampleDerivativeX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= cur / d;
    }
    return sampleCurveY(t);
  };
}
const fadeEasingFn = cubicBezierEasing(0.85, 0, 0.95, 0.2);

let active = false;

export function showIntroVideo(src: string = INTRO_VIDEO_FIRST): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  // Guard against overlapping overlays — if one is already playing,
  // ignore the new request rather than stacking videos on top of
  // each other.
  if (active) return Promise.resolve();
  // Skip in dev/test contexts — `?dev=1` URL param indicates either
  // the dev console (manual debugging) or an automated Playwright
  // run. The 10 s video adds zero value there and was racing first-
  // input handlers in MP tests (intercepting H key as unmute) and
  // covering the boss-death banner (z=10001 vs banner z=1500).
  // Production users hit this code path with no `?dev=1` and see
  // the cinematic normally.
  try {
    const params = new URL(location.href).searchParams;
    if (params.get('dev') === '1' || params.get('skipIntro') === '1') {
      return Promise.resolve();
    }
  } catch { /* SSR / no location — fall through */ }
  active = true;

  // Hard-mute the music bus through the intro mute lock. Plain
  // `setMusicDuck(0)` would be overridden every frame by app.ts's
  // per-frame duck smoother; this lock multiplies through and stays
  // until we explicitly clear it.
  setIntroMusicMute(true);

  return new Promise<void>((resolve) => {
    const root = document.createElement('div');
    root.className = 'ui smp-intro-video';
    root.style.cssText = `
      position: fixed; inset: 0;
      z-index: 10001;
      background: #000;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer;
      overflow: hidden;
    `;

    const video = document.createElement('video');
    video.src = src;
    video.autoplay = true;
    // Muted by default — browsers universally allow muted autoplay.
    // The first user input is intercepted below to unmute (instead
    // of skipping). After that, any input skips.
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    // 16:9 — letterbox into whatever window aspect the user has.
    video.style.cssText = `
      width: 100%; height: 100%;
      object-fit: contain;
      display: block;
    `;

    const skipHint = document.createElement('div');
    skipHint.textContent = '🔇 PRESS ANY KEY FOR SOUND';
    skipHint.style.cssText = `
      position: absolute;
      bottom: max(20px, env(safe-area-inset-bottom));
      left: 50%;
      transform: translateX(-50%);
      padding: 6px 14px;
      background: rgba(8,10,18,.65);
      border-radius: 999px;
      color: #cbd5e1;
      font: 700 10px ui-monospace, "SF Mono", monospace;
      letter-spacing: 2.2px;
      pointer-events: none;
      z-index: 2;
    `;

    root.appendChild(video);
    root.appendChild(skipHint);
    document.body.appendChild(root);

    let closed = false;
    let fadeStarted = false;

    const finish = () => {
      if (closed) return;
      closed = true;
      removeEventListener('keydown', onKey);
      removeEventListener('mousedown', onMouse);
      removeEventListener('touchstart', onTouch);
      try { video.pause(); } catch { /* noop */ }
      // Restore music with a quick fade-in so a skip doesn't snap the
      // soundtrack on at full volume. Idempotent if the natural fade
      // already started the ramp — the fade helper cancels any prior
      // ramp before starting a new one.
      setIntroMusicMute(false, fadeStarted ? 0 : MUSIC_FADE_SKIP_MS);
      const finalize = () => { root.remove(); active = false; resolve(); };
      // If natural fade already started, root is mid-transition or
      // fully transparent — just remove. Otherwise fade quickly so the
      // skip doesn't strobe.
      if (!fadeStarted) {
        root.style.transition = 'opacity .22s ease-out';
        root.style.opacity = '0';
        setTimeout(finalize, 240);
      } else {
        finalize();
      }
    };

    const startNaturalFade = () => {
      if (fadeStarted || closed) return;
      fadeStarted = true;
      // Ramp the game music in over the same window as the video
      // opacity fade so they cross seamlessly.
      setIntroMusicMute(false, MUSIC_FADE_NATURAL_MS);
      root.style.transition = `opacity ${FADE_DURATION_SEC}s ${FADE_EASING}`;
      root.style.opacity = '0';
      // Fade the VIDEO's own audio along the EXACT same curve as the
      // opacity. CSS can't transition `video.volume`, so we drive it
      // by rAF using the cubic-bezier evaluator above — same easing,
      // same window, same pace.
      const startVol = video.volume;
      const startAt = performance.now();
      const fadeMs = FADE_DURATION_SEC * 1000;
      const audioRamp = (): void => {
        if (closed) return;
        const t = Math.min(1, (performance.now() - startAt) / fadeMs);
        const eased = fadeEasingFn(t);
        try { video.volume = startVol * (1 - eased); } catch { /* noop */ }
        if (t < 1) requestAnimationFrame(audioRamp);
      };
      requestAnimationFrame(audioRamp);
    };

    // First input unmutes (consumed); subsequent inputs skip. Modifier-
    // only keys (Cmd/Ctrl/Alt/Shift) are ignored entirely so a stray
    // shortcut press doesn't kill the intro or count as the unmute
    // gesture.
    const onAnyInput = () => {
      if (video.muted) {
        video.muted = false;
        // Some browsers pause when muted toggles mid-playback — re-
        // call play(). If even unmuted play is rejected for some
        // reason, restore the muted attribute so the video keeps
        // playing visually.
        void video.play().catch(() => { video.muted = true; });
        skipHint.textContent = 'PRESS ANY KEY TO SKIP';
        return;
      }
      finish();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Meta' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Shift') return;
      onAnyInput();
    };
    const onMouse = () => onAnyInput();
    const onTouch = () => onAnyInput();
    addEventListener('keydown', onKey);
    addEventListener('mousedown', onMouse);
    addEventListener('touchstart', onTouch, { passive: true });

    video.addEventListener('timeupdate', () => {
      if (video.currentTime >= FADE_START_SEC) startNaturalFade();
    });
    video.addEventListener('ended', finish);
    video.addEventListener('error', finish);

    // Hard-cap fallback in case the video stalls or 404s.
    setTimeout(finish, MAX_LIFETIME_MS);

    // Muted autoplay — universally allowed. Sound waits for the first
    // user input (handled in onAnyInput).
    void video.play().catch(() => { /* dead — let timeupdate / hard-cap finish */ });
  });
}
